import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from 'react-i18next';
import { FaArrowRight, FaTimes } from 'react-icons/fa';
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

// New Autonomous Components with Overlay Exports
import HeroPreview, { HeroOverlay } from './InvitoDigitaleCard/HeroPreview';
import LocationPreview, { LocationOverlay } from './InvitoDigitaleCard/LocationPreview';
import RSVPPreview, { RSVPOverlay } from './InvitoDigitaleCard/RSVPPreview';

import cerimoniaImg from './img/cerimonia.jpg';

gsap.registerPlugin(ScrollTrigger);

// --- CONFIGURATION & DATA ---

const FEATURES_DATA = [
    {
        id: 1,
        type: 'hero',
        isFullWidth: true,
        title: "L'Impatto",
        microCopy: "Immersione visiva",
        overlayTitle: "Storytelling",
        description: "Un'apertura quasi cinematografica che catturi l'attenzione con semplicità, eleganza e fascino.",
        bullets: [
            "Narrazione visiva con animazioni fluide e naturali",
            "Esperienza utente focalizzata sull'emozione",
            "Design full-screen e tipografia ricercata",
            "Codice QR da integrare nell'invito cartaceo"
        ],
        data: {
            title: "Romeo & Giulietta",
            date: "07 AGOSTO 2025"
        }
    },
    {
        id: 3,
        type: 'location',
        title: "La Guida",
        microCopy: "Accompagnare, non solo indicare",
        overlayTitle: "Location & Maps",
        description: "Guida gli ospiti verso la meta con mappe interattive chiare, eleganti e funzionali.",
        bullets: [
            "Embed Google Maps ottimizzato",
            "Interfaccia pulita e leggibile",
            "Focus sulla reperibilità",
            "Layout responsive adattivo"
        ],
        data: [
            {
                title: "Cerimonia",
                text: "La cerimonia si terrà presso la Chiesa di San Pietro, un luogo ricco di storia e magia.",
                image: cerimoniaImg,
                mapUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2945.704484766788!2d11.8784201!3d42.4127374!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1328daa530e50d81%3A0x13a732a06159387a!2sBasilica%20di%20San%20Pietro%20-%20Tuscania!5e0!3m2!1sit!2sit!4v1708545723961!5m2!1sit!2sit&maptype=hybrid"
            },
            {
                title: "Ricevimento",
                text: "Dopo la cerimonia, vi aspettiamo a Villa Monteverdi per festeggiare insieme.",
                image: "https://cdn.pixabay.com/photo/2019/12/19/09/07/deco-4705709_1280.jpg",
                mapUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2882.1780533143365!2d10.9871033!3d43.748399199999994!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x132a5d941c81cc0d%3A0xe80a075b5f56e860!2sVilla%20Monteverdi!5e0!3m2!1sit!2sit!4v1711880078841!5m2!1sit!2sit"
            }
        ]
    },
    {
        id: 4,
        type: 'rsvp',
        title: "Il Sì",
        microCopy: "Confermare senza ostacoli",
        overlayTitle: "Sistema RSVP",
        description: "Un'esperienza di conferma presenza senza frizioni, digitale e immediata.",
        bullets: [
            "Form snello e intuitivo",
            "Stile cartaceo digitalizzato",
            "Nessun login richiesto",
            "Feedback visivo rassicurante"
        ],
        data: {
            bgPattern: "https://www.transparenttextures.com/patterns/cream-paper.png",
            title: "RSVP"
        }
    }
];

// --- MAIN COMPONENT ---

const DigitalInvite = () => {
    const { t } = useTranslation();
    const containerRef = useRef(null);
    const showcaseRef = useRef(null);
    const overlayRef = useRef(null);
    const [selectedId, setSelectedId] = useState(null);

    const selectedFeature = FEATURES_DATA.find(f => f.id === selectedId);

    // Initial Animations
    useLayoutEffect(() => {
        let ctx = gsap.context(() => {
            // Header Text
            gsap.from(".anim-title", {
                y: 50, opacity: 0, duration: 1, ease: "power3.out", stagger: 0.2,
                scrollTrigger: { trigger: containerRef.current, start: "top 70%" }
            });

            // Card Blocks Entrance
            gsap.from(".block-container", {
                y: 60, opacity: 0, duration: 1.2, stagger: 0.15, ease: "power3.out",
                scrollTrigger: { trigger: showcaseRef.current, start: "top 80%" }
            });

        }, [containerRef, showcaseRef]);
        return () => ctx.revert();
    }, []);

    // Overlay Animation
    useLayoutEffect(() => {
        if (!selectedId || !overlayRef.current) return;
        let ctx = gsap.context(() => {
            gsap.fromTo(".overlay-backdrop", { opacity: 0 }, { opacity: 1, duration: 0.4, ease: "power2.out" });
            gsap.fromTo(".overlay-content", { opacity: 0, y: 30, scale: 0.95 }, { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: "back.out(1.2)", delay: 0.1 });
        }, overlayRef);
        return () => ctx.revert();
    }, [selectedId]);

    // Lock Scroll
    useEffect(() => {
        document.body.style.overflow = selectedId ? 'hidden' : 'unset';
        return () => { document.body.style.overflow = 'unset'; };
    }, [selectedId]);

    const handleCardClick = (id) => setSelectedId(id);
    const handleClose = () => setSelectedId(null);

    return (
        <section id="invite" ref={containerRef} className="py-24 bg-white text-slate-800 font-Crimson relative overflow-hidden">
            {/* UNIFIED TITLE & INTRO */}
            <div className="container mx-auto px-6 max-w-5xl mb-12 text-center md:text-left">
                <h1 className="anim-title text-4xl md:text-6xl font-bold mb-8 leading-tight">{t('invite_page.title')}</h1>
                <p className="anim-title text-lg md:text-xl text-slate-600 font-light max-w-2xl leading-relaxed mx-auto md:mx-0">{t('invite_page.intro')}</p>
            </div>

            {/* CARD SWIPER SECTION */}
            <div className="w-full max-w-[1400px] mx-auto px-4 md:px-10" ref={showcaseRef}>
                <Swiper
                    modules={[Pagination]}
                    spaceBetween={30}
                    slidesPerView={1}
                    breakpoints={{
                        640: { slidesPerView: 1.5, centeredSlides: true },
                        1024: { slidesPerView: 3, centeredSlides: false }
                    }}
                    pagination={{ clickable: true, dynamicBullets: true }}
                    className="!pb-20"
                >
                    {FEATURES_DATA.map((feature) => (
                        <SwiperSlide key={feature.id} className="h-auto">
                            <div className="block-container flex flex-col gap-4 items-center md:items-start h-full pb-2">
                                {/* HEADER */}
                                <div className="flex flex-col gap-1 mb-2 pl-1 w-full text-center md:text-left">
                                    <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-medium">{feature.microCopy}</p>
                                </div>

                                {/* CARD */}
                                <div
                                    onClick={() => handleCardClick(feature.id)}
                                    className={`
                                        group relative cursor-pointer overflow-hidden rounded-2xl
                                        bg-white shadow-xl border border-slate-100
                                        transition-all duration-500 ease-out transform
                                        hover:shadow-2xl hover:scale-[1.01] hover:border-lime-300/50
                                        h-[500px] w-[290px] mx-auto md:mx-0
                                    `}
                                >
                                    {/* Component Preview Container */}
                                    <div className="relative w-full h-full">
                                        {feature.type === 'hero' && <HeroPreview data={feature.data} />}
                                        {feature.type === 'location' && <LocationPreview data={feature.data} />}
                                        {feature.type === 'rsvp' && <RSVPPreview data={feature.data} />}
                                    </div>

                                    {/* CTA Button (Inside Card) */}
                                    <div
                                        className={`
                                            absolute bottom-6 right-6 z-30 
                                            flex items-center gap-3 py-2 px-4 rounded-full backdrop-blur-md
                                            text-xs font-bold uppercase tracking-widest transition-all duration-300
                                            ${feature.type === 'hero'
                                                ? 'bg-white/10 text-white border border-white/30 hover:bg-white hover:text-slate-900'
                                                : 'bg-white/80 text-slate-600 border border-slate-200 hover:bg-lime-600 hover:text-white hover:border-lime-600'
                                            }
                                        `}
                                    >
                                        <span>Scopri</span>
                                        <FaArrowRight />
                                    </div>
                                </div>
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>

            {/* MODAL OVERLAY */}
            {selectedId && selectedFeature && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" ref={overlayRef}>
                    <div className="overlay-backdrop absolute inset-0 bg-transparent" onClick={handleClose} />

                    {/* Compact Detail Card */}
                    <div className="overlay-content w-full max-w-lg bg-white rounded-3xl shadow-2xl relative overflow-hidden flex flex-col">
                        <button onClick={handleClose} className="absolute top-4 right-4 z-50 w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 transition-all">
                            <FaTimes size={12} />
                        </button>

                        {/* Dynamic Content based on Type */}
                        <div className="p-8 md:p-10">
                            {selectedFeature.type === 'hero' && <HeroOverlay data={selectedFeature} />}
                            {selectedFeature.type === 'location' && <LocationOverlay data={selectedFeature} />}
                            {selectedFeature.type === 'rsvp' && <RSVPOverlay data={selectedFeature} />}
                        </div>

                    </div>
                </div>
            )}

            <p className="anim-title text-center text-lg text-slate-400 font-light mt-8">
                {t('invite_page.demo_text')} <a href="https://cisposiamodemo.netlify.app/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Link</a>
            </p>
        </section>
    );
};

export default DigitalInvite;
